@if (!isUserLoading) {
    <div>
        <sb-user-avatar class="user-avatar-lg mx-auto" [user]="user" [size]="'large'" [isNeedTooltip]="false">
        </sb-user-avatar>
        <div class="break-word mt-4">
            <div class="d-flex justify-content-between mb-2">
                <h3 class="text-break mb-0">{{ user.name }}</h3>

                @if (isCanBeEdited && !isEditFormOpened) {
                    <button
                        class="btn btn-icon"
                        (click)="openUserEditForm()"
                        tooltip="{{ 'Click to edit user name and email' | translate }}"
                        container="body">
                        <sb-inline-svg
                            class="icon"
                            [url]="'/sb-tools/theme/images/Svg icons WEB/Web icons/Pen icon.svg'">
                        </sb-inline-svg>
                    </button>
                }
            </div>

            @if (isEditFormOpened) {
                <div>
                    <hr />
                    <form #userForm="ngForm" [ngClass]="{ 'submitted form-submitted': isSubmitted }">
                        <div class="form-group">
                            <label for="user-name" translate>Name</label>
                            <input
                                autocomplete="off"
                                type="text"
                                class="form-control"
                                id="user-name"
                                placeholder="{{ 'User name' | translate }}"
                                [(ngModel)]="userModel.name"
                                name="userName"
                                sbTrim
                                appFocus
                                [appAutoFocus]="true"
                                maxlength="256" />
                        </div>
                        <div class="form-group">
                            <label for="user-email" translate>Email</label>
                            <input
                                autocomplete="off"
                                [pattern]="emailValidationPattern"
                                class="form-control"
                                id="user-email"
                                placeholder="{{ 'User email' | translate }}"
                                [(ngModel)]="userModel.email"
                                name="userEmail"
                                sbTrim
                                #field="ngModel"
                                maxlength="256" />

                            @if (isSubmitted && userModel.email && field.errors?.pattern) {
                                <div class="text-danger" translate>Please enter a valid email address</div>
                            }
                        </div>
                        <div class="d-flex justify-content-end">
                            <button
                                [disabled]="isUpdating"
                                type="button"
                                class="btn btn-default me-2"
                                (click)="isEditFormOpened = false"
                                translate>
                                Cancel
                            </button>
                            <button
                                [disabled]="isUpdating"
                                type="submit"
                                class="btn btn-primary"
                                (click)="updateUserInfo()"
                                translate>
                                Save
                            </button>
                        </div>
                    </form>
                    <hr />
                </div>
            }

            @if (user.email) {
                <div class="mb-2 d-flex justify-content-between w-100 copy-block">
                    <a href="mailto:{{ user.email }}" class="copy-field text-break">
                        {{ user.email }}
                    </a>
                    <div
                        class="copy-block-button"
                        container="body"
                        placement="left"
                        [adaptivePosition]="false"
                        [tooltip]="'Copied' | translate"
                        [tooltipFadeDuration]="1000"
                        [triggers]="''"
                        #emailTooltip="bs-tooltip">
                        <button
                            class="btn btn-icon"
                            ngxClipboard
                            [cbContent]="user.email"
                            (click)="emailTooltip.show(); emailTooltip.hide()">
                            <sb-inline-svg
                                class="icon"
                                aria-label="My icon"
                                [url]="'/sb-tools/theme/images/Svg icons WEB/Web icons/Copy icon.svg'"></sb-inline-svg>
                        </button>
                    </div>
                </div>
            }

            <div class="mb-2">{{ user.role | roleName }}</div>
            @if (user.phoneNumber) {
                <div class="mb-2">{{ user.phoneNumber }}</div>
            }

            @if (user.socialMediaAccountInfo) {
                <div class="d-block mb-2">
                    {{ user.socialMediaAccountInfo.source | ticketSource }}:
                    <a [href]="user.socialMediaAccountInfo.permalink" target="_blank">{{
                        user.socialMediaAccountInfo.accountName || user.name
                    }}</a>
                </div>
            }
            <div>
                <label
                    class="mb-2 label label-simple"
                    [hidden]="!user.creationTime"
                    tooltip="{{ user.creationTime | date: 'dd.MM.yyyy HH:mm' }}">
                    {{ 'Joined' | translate }} {{ user.creationTime | timeAgo }}
                </label>
            </div>
            @if (user.blockingInfo?.isBlocked) {
                <div class="mb-2 alert alert-warning">
                    {{
                        ('User was blocked' | translate) +
                            ' ' +
                            (user.blockingInfo.blockingDate | date: 'dd.MM.yyyy') +
                            ' ' +
                            ('by' | translate)
                    }}
                    <a
                        (click)="rightPanelService.open(rightPanelTypeEnum.user, { user: user.blockingInfo.blockedBy })"
                        >{{ user.blockingInfo.blockedBy.name || user.blockingInfo.blockedBy.email }}</a
                    >
                    &nbsp;
                    <a target="_blank" href="{{ user.blockingInfo.permalink }}" translate>permalink</a>
                </div>
            }
            @if (fields.length > 0 && fieldsService.ticketCreatorId === userId) {
                <div class="mt-3 mb-3">
                    @for (field of fields; track field.id) {
                        <div class="d-flex justify-content-between w-100 copy-block">
                            <div class="field">{{ field.name }}: {{ field.transformedValue }}</div>
                            <div
                                class="d-inline-block copy-block-button"
                                container="body"
                                placement="left"
                                [adaptivePosition]="false"
                                [tooltip]="'Copied' | translate"
                                [tooltipFadeDuration]="1000"
                                [triggers]="''"
                                #fieldTooltip="bs-tooltip">
                                <button
                                    class="btn btn-icon"
                                    ngxClipboard
                                    [cbContent]="field.transformedValue"
                                    (click)="fieldTooltip.show(); fieldTooltip.hide()">
                                    <sb-inline-svg
                                        class="icon"
                                        aria-label="My icon"
                                        [url]="'/sb-tools/theme/images/Svg icons WEB/Web icons/Copy icon.svg'">
                                    </sb-inline-svg>
                                </button>
                            </div>
                        </div>
                    }
                </div>
            }

            <br />
            <div>
                <p>{{ 'Previous tickets' | translate }}</p>
                @for (userLastTicket of userLastTickets; track userLastTicket) {
                    <div class="linked-ticket-item">
                        <div class="linked-ticket-item-text d-flex">
                            <a
                                (click)="openTicketInNewWindowService.openTicketInNewWindow(userLastTicket.ticketId)"
                                class="flex-grow-1 truncate pe-2">
                                {{ userLastTicket.text || '...' }}
                            </a>
                            {{ userLastTicket.creationTime | date: 'dd.MM.yyyy' }}
                        </div>
                        <hr />
                    </div>
                }
                @if (userLastTickets?.length > 0) {
                    <a
                        [routerLink]="['/', commonDataService.communityName, 'tickets']"
                        [queryParams]="{ createdBy: user.id }"
                        target="_blank"
                        routerLinkActive="active"
                        translate
                        >All previous tickets</a
                    >
                } @else {
                    <div class="text-light" translate>No tickets</div>
                }
            </div>
            <br />
        </div>
    </div>
}
